import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import confetti from 'canvas-confetti';
import {
  Trophy,
  Sparkles,
  CheckCircle2,
  XCircle,
  Clock,
  Flame,
  Lock,
} from 'lucide-react';
import { useApi } from '../../hooks/useApi';
import { useAuth } from '../../auth/AuthProvider';
import { apiSend, ApiError } from '../../lib/api';
import { Skeleton } from '../../components/Skeleton';
import type {
  LeaderboardResponse,
  PuzzleSolveResult,
  PuzzleTodayResponse,
  StreakResponse,
} from '../puzzleTypes';

// ─── helpers ───────────────────────────────────────────────────────────

const fireConfetti = () => {
  // Twin bursts from each lower corner so it shoots up across the card,
  // not just a centre dot. ~1.2s of motion total, gentle on performance.
  const end = Date.now() + 800;
  const colors = ['#FF6600', '#0B1272', '#10B981', '#F472B6'];
  (function frame() {
    confetti({
      particleCount: 4,
      angle: 60,
      spread: 55,
      origin: { x: 0, y: 0.85 },
      colors,
    });
    confetti({
      particleCount: 4,
      angle: 120,
      spread: 55,
      origin: { x: 1, y: 0.85 },
      colors,
    });
    if (Date.now() < end) requestAnimationFrame(frame);
  })();
};

const useCountdownToMidnight = () => {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(id);
  }, []);
  const midnight = useMemo(() => {
    const m = new Date(now);
    m.setHours(24, 0, 0, 0);
    return m;
  }, [now]);
  const diff = Math.max(0, midnight.getTime() - now.getTime());
  const h = Math.floor(diff / 3_600_000);
  const m = Math.floor((diff % 3_600_000) / 60_000);
  return `${h}h ${m.toString().padStart(2, '0')}m`;
};

// ─── Puzzle card ───────────────────────────────────────────────────────

const PuzzleCard = ({
  data,
  onSolved,
}: {
  data: PuzzleTodayResponse;
  onSolved: (result: PuzzleSolveResult) => void;
}) => {
  const { getToken } = useAuth();
  const [picked, setPicked] = useState<number | null>(null);
  const [answer, setAnswer] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const startedAt = useRef(Date.now());
  const countdown = useCountdownToMidnight();

  const solve = data.solve;
  const locked = solve !== null;

  // Reset start-timer when puzzle changes (new day).
  useEffect(() => {
    startedAt.current = Date.now();
  }, [data.puzzle.id]);

  const submit = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const token = await getToken();
      const pickedAnswer =
        data.puzzle.kind === 'mcq' ? String(picked ?? '') : answer.trim();
      const result = await apiSend<PuzzleSolveResult>(
        'POST',
        '/api/puzzle/solve',
        { pickedAnswer, timeToSolveMs: Date.now() - startedAt.current },
        token,
      );
      onSolved(result);
      if (result.isCorrect) fireConfetti();
    } catch (err) {
      // 409 already_solved → adopt the server's solve into local state.
      if (err instanceof ApiError && err.status === 409) {
        const body = err.body as { solve?: PuzzleSolveResult };
        if (body?.solve) onSolved(body.solve);
        else setError('Already answered today.');
      } else {
        setError("Couldn't submit. Try again.");
      }
    } finally {
      setBusy(false);
    }
  }, [data.puzzle.kind, picked, answer, getToken, onSolved]);

  // After submit, picked/answer mirror what the server stored so the UI
  // highlights the right row.
  const displayPicked =
    data.puzzle.kind === 'mcq' && locked && solve
      ? Number(solve.pickedAnswer)
      : picked;

  return (
    <div className="flex h-full flex-col rounded-xl border border-line bg-bg-primary p-4 shadow-sm">
      <div className="mb-3 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <div className="grid h-8 w-8 place-items-center rounded-lg bg-brand-orange/15 text-brand-orange">
            <Sparkles className="h-4 w-4" />
          </div>
          <h3 className="text-[11px] font-semibold uppercase tracking-wider text-text-secondary">
            Puzzle of the day
          </h3>
        </div>
        <span className="inline-flex items-center gap-1 text-[10px] text-text-tertiary">
          <Clock className="h-3 w-3" /> {data.puzzle.durationMin} min
        </span>
      </div>

      <p className="text-sm text-text-primary">{data.puzzle.prompt}</p>

      <div className="mt-3 flex-1 space-y-1.5">
        {data.puzzle.kind === 'mcq' && data.puzzle.options ? (
          data.puzzle.options.map((opt, i) => {
            const isPicked = displayPicked === i;
            const isCorrectAns =
              locked && solve && Number(solve.correct) === i;
            const isPickedWrong = locked && isPicked && !solve!.isCorrect;
            return (
              <button
                key={i}
                type="button"
                disabled={locked || busy}
                onClick={() => setPicked(i)}
                className={[
                  'flex w-full items-center justify-between gap-2 rounded-md border px-3 py-2 text-left text-sm transition-colors',
                  'min-h-11 disabled:cursor-default',
                  isCorrectAns
                    ? 'border-status-healthy bg-status-healthy/10 text-status-healthy'
                    : isPickedWrong
                    ? 'border-status-critical bg-status-critical/10 text-status-critical'
                    : isPicked
                    ? 'border-brand-navy bg-brand-navy/5 text-text-primary'
                    : 'border-line bg-bg-secondary text-text-secondary hover:border-brand-navy/40 hover:text-text-primary',
                ].join(' ')}
              >
                <span>{opt}</span>
                {isCorrectAns && <CheckCircle2 className="h-4 w-4" />}
                {isPickedWrong && <XCircle className="h-4 w-4" />}
              </button>
            );
          })
        ) : (
          <input
            type="text"
            value={locked && solve ? String(solve.pickedAnswer) : answer}
            onChange={(e) => setAnswer(e.target.value)}
            disabled={locked || busy}
            placeholder="Type your answer…"
            className="block w-full rounded-md border border-line bg-bg-secondary px-3 py-2 text-sm text-text-primary placeholder:text-text-tertiary focus:border-brand-navy focus:outline-none focus:ring-2 focus:ring-brand-navy/30 disabled:opacity-70"
          />
        )}
      </div>

      {error && (
        <div className="mt-3 rounded-md border border-status-critical/30 bg-status-critical/10 px-3 py-2 text-xs text-status-critical">
          {error}
        </div>
      )}

      <div className="mt-3 flex items-center justify-between gap-2">
        {locked ? (
          <div className="flex items-center gap-1.5 text-[11px] text-text-tertiary">
            <Lock className="h-3 w-3" /> Next puzzle in {countdown}
          </div>
        ) : (
          <div className="text-[11px] text-text-tertiary">
            One shot — answers lock in until midnight.
          </div>
        )}
        {!locked ? (
          <button
            type="button"
            onClick={submit}
            disabled={busy || (picked === null && !answer.trim())}
            className="rounded-md bg-brand-orange px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-brand-orange-dark disabled:cursor-not-allowed disabled:opacity-50"
          >
            {busy ? 'Submitting…' : 'Submit'}
          </button>
        ) : (
          <span
            className={`text-xs font-semibold ${
              solve!.isCorrect ? 'text-status-healthy' : 'text-status-critical'
            }`}
          >
            {solve!.isCorrect ? `Correct! +${solve!.points} pts` : 'Not quite.'}
          </span>
        )}
      </div>

      {locked && solve && (
        <div className="mt-2 rounded-md border border-line bg-bg-secondary px-3 py-2 text-[11px] text-text-secondary">
          <span className="font-semibold text-text-primary">Why:</span> {solve.explanation}
        </div>
      )}
    </div>
  );
};

// ─── Top scorers card (live from /leaderboard) ─────────────────────────

const RANK_TINT = (rank: number): string => {
  if (rank === 1) return 'bg-status-warning/20 text-status-warning';
  if (rank === 2) return 'bg-bg-tertiary text-text-secondary';
  if (rank === 3) return 'bg-brand-orange/15 text-brand-orange';
  return 'bg-bg-tertiary text-text-tertiary';
};

const TopScorerCard = ({
  lb,
}: {
  lb: ReturnType<typeof useApi<LeaderboardResponse>>;
}) => {

  if (lb.loading) {
    return (
      <div className="rounded-xl border border-line bg-bg-primary p-4 shadow-sm">
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  if (!lb.data || lb.data.top.length === 0) {
    return (
      <div className="flex h-full flex-col rounded-xl border border-line bg-bg-primary p-4 shadow-sm">
        <div className="mb-3 flex items-center gap-2">
          <div className="grid h-8 w-8 place-items-center rounded-lg bg-status-warning/15 text-status-warning">
            <Trophy className="h-4 w-4" />
          </div>
          <h3 className="text-[11px] font-semibold uppercase tracking-wider text-text-secondary">
            Today's top scorers
          </h3>
        </div>
        <p className="text-sm text-text-secondary">
          No one's on the board yet today. Solve the puzzle to be first 🚀
        </p>
        {lb.data?.me && (
          <div className="mt-3 text-[11px] text-text-tertiary">
            You: <span className="font-semibold text-text-secondary">{lb.data.me.points} pts</span>
            {lb.data.me.solves > 0 ? ` · ${lb.data.me.solves} solves` : ''}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col rounded-xl border border-line bg-bg-primary p-4 shadow-sm">
      <div className="mb-3 flex items-center gap-2">
        <div className="grid h-8 w-8 place-items-center rounded-lg bg-status-warning/15 text-status-warning">
          <Trophy className="h-4 w-4" />
        </div>
        <h3 className="text-[11px] font-semibold uppercase tracking-wider text-text-secondary">
          Today's top scorers
        </h3>
      </div>
      <ol className="space-y-1.5">
        {lb.data.top.slice(0, 5).map((row) => (
          <li
            key={row.userId}
            className="flex items-center justify-between gap-2 rounded-md px-2 py-1.5 text-sm"
          >
            <div className="flex min-w-0 items-center gap-2">
              <span
                className={`grid h-6 w-6 shrink-0 place-items-center rounded-full text-[11px] font-semibold tabular-nums ${RANK_TINT(row.rank)}`}
              >
                {row.rank}
              </span>
              <span className="truncate text-text-primary">{row.displayName}</span>
            </div>
            <span className="shrink-0 text-[11px] font-semibold tabular-nums text-text-primary">
              {row.points} pts
            </span>
          </li>
        ))}
      </ol>
      <div className="mt-auto pt-3 text-[11px] text-text-tertiary">
        You:{' '}
        <span className="font-semibold text-text-secondary">
          {lb.data.me.points} pts
          {lb.data.me.rank ? ` · rank ${lb.data.me.rank}` : ''}
        </span>
      </div>
    </div>
  );
};

// ─── Streak badge — sits above the puzzle ──────────────────────────────

const StreakBadge = ({
  streak,
}: {
  streak: ReturnType<typeof useApi<StreakResponse>>;
}) => {
  if (streak.loading || !streak.data) return null;
  const { current, longest, solvedToday, totalPoints } = streak.data;
  if (current === 0 && totalPoints === 0) return null;
  return (
    <div className="flex flex-wrap items-center gap-3 text-[11px] text-text-tertiary">
      {current > 0 && (
        <span className="inline-flex items-center gap-1">
          <Flame className="h-3.5 w-3.5 text-status-warning" aria-hidden="true" />
          <span className="font-semibold text-text-secondary">{current}-day streak</span>
          {longest > current && <span>· longest {longest}</span>}
        </span>
      )}
      {totalPoints > 0 && (
        <span className="inline-flex items-center gap-1">
          <span className="font-semibold text-text-secondary">{totalPoints} pts</span>
          <span>total</span>
        </span>
      )}
      {!solvedToday && current > 0 && (
        <span className="rounded-full bg-status-warning/15 px-2 py-0.5 font-semibold text-status-warning">
          Don't break it!
        </span>
      )}
    </div>
  );
};

// ─── Composite ─────────────────────────────────────────────────────────

export const FunZone = () => {
  const today = useApi<PuzzleTodayResponse>('/api/puzzle/today');
  const lb = useApi<LeaderboardResponse>('/api/puzzle/leaderboard?scope=day');
  const streak = useApi<StreakResponse>('/api/puzzle/streak');

  const onSolved = useCallback(
    (result: PuzzleSolveResult) => {
      // Flip /today to locked immediately (no round-trip).
      today.mutate((prev) => (prev ? { ...prev, solve: result } : prev));
      // Refetch leaderboard + streak so the user sees their new rank + pts.
      // Leaderboard view: even if the user was already on the board (week/all
      // scope hypothetically), today's points shift their position.
      lb.refetch();
      streak.refetch();
    },
    [today, lb, streak],
  );

  return (
    <section
      aria-label="Fun zone"
      className="rounded-2xl border border-brand-orange/20 bg-brand-orange/[0.04] p-4 sm:p-5"
    >
      <div className="mb-3 flex items-center justify-between gap-3">
        <h2 className="text-sm font-semibold tracking-tight text-text-primary">🎯 Fun Zone</h2>
        <StreakBadge streak={streak} />
      </div>
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        {today.loading ? (
          <Skeleton className="h-48 w-full" />
        ) : today.data ? (
          <PuzzleCard data={today.data} onSolved={onSolved} />
        ) : (
          <div className="rounded-xl border border-line bg-bg-primary p-4 text-sm text-text-tertiary">
            Couldn't load today's puzzle.
          </div>
        )}
        <TopScorerCard lb={lb} />
      </div>
    </section>
  );
};
